import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Edit3, RefreshCw, Save, X } from 'lucide-react';
import {
  adminGetPlans,
  adminUpsertPlan,
  adminTogglePlan,
  adminSeedPlans,
} from '../../services/mockInterviewService';
import { useNotification } from '../../notification/NotificationContext';
import './AdminDashboard.css';

const blankPlan = () => ({
  id: 0,
  planKey: '',
  displayName: '',
  priceMonthlyPaise: 0,
  priceYearlyPaise: 0,
  minutesAllowed: 0,
  features: [],
  isActive: true,
  sortOrder: 0,
});

const AdminPlansPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // plan object or null
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = () => {
    setLoading(true);
    adminGetPlans()
      .then((d) => setPlans(Array.isArray(d) ? d : []))
      .catch((e) => showNotification(e.message || 'Failed to load plans', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const openEdit = (plan) => setEditing(plan ? { ...plan, features: [...(plan.features || [])] } : blankPlan());
  const closeEdit = () => setEditing(null);

  const updateField = (field, value) => setEditing((p) => ({ ...p, [field]: value }));

  const addFeature = () => setEditing((p) => ({ ...p, features: [...(p.features || []), ''] }));
  const updateFeature = (i, value) =>
    setEditing((p) => ({
      ...p,
      features: p.features.map((f, idx) => (idx === i ? value : f)),
    }));
  const removeFeature = (i) =>
    setEditing((p) => ({
      ...p,
      features: p.features.filter((_, idx) => idx !== i),
    }));

  const submit = async () => {
    const p = editing;
    if (!p?.planKey?.trim() || !p?.displayName?.trim()) {
      showNotification('Plan key and display name are required', 'warning');
      return;
    }
    setSaving(true);
    try {
      const cleanFeatures = (p.features || []).map((f) => f.trim()).filter(Boolean);
      await adminUpsertPlan({
        planKey: p.planKey.trim(),
        displayName: p.displayName.trim(),
        priceMonthlyPaise: Number(p.priceMonthlyPaise) || 0,
        priceYearlyPaise: Number(p.priceYearlyPaise) || 0,
        minutesAllowed: Number(p.minutesAllowed) || 0,
        features: cleanFeatures,
        sortOrder: Number(p.sortOrder) || 0,
        isActive: !!p.isActive,
      });
      showNotification('Plan saved', 'success');
      closeEdit();
      load();
    } catch (e) {
      showNotification(e.message || 'Failed to save plan', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (plan) => {
    try {
      await adminTogglePlan(plan.id);
      showNotification(`Plan ${plan.isActive ? 'disabled' : 'enabled'}`, 'success');
      load();
    } catch (e) {
      showNotification(e.message || 'Failed to toggle plan', 'error');
    }
  };

  const handleSeed = async () => {
    if (!window.confirm('Seed the default plans? Existing plans with the same plan key will be overwritten.')) return;
    setSeeding(true);
    try {
      await adminSeedPlans();
      showNotification('Default plans seeded', 'success');
      load();
    } catch (e) {
      showNotification(e.message || 'Failed to seed plans', 'error');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="adm-shell adm-shell-home">
      <header className="adm-header">
        <div className="adm-logo">
          <span className="adm-logo-icon">💳</span>
          <span>Subscription Plans</span>
        </div>
        <div className="adm-header-right">
          <button className="adm-logout" onClick={() => navigate('/admin/dashboard')}>← Dashboard</button>
          <button className="adm-logout" onClick={handleLogout}>Logout →</button>
        </div>
      </header>

      <main className="adm-home" style={{ paddingTop: 24 }}>
        <section className="adm-hero" style={{ marginBottom: 18 }}>
          <div>
            <p className="adm-kicker">Pricing config</p>
            <h1 className="adm-hero-title">Manage subscription tiers</h1>
            <p className="adm-hero-sub">Edit prices, toggle availability, and define feature lists.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="adm-logout" onClick={handleSeed} disabled={seeding}>
              <RefreshCw size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              {seeding ? 'Seeding…' : 'Seed Defaults'}
            </button>
            <button className="adm-logout" onClick={() => openEdit(null)} style={{ background: 'rgba(99,102,241,0.18)', borderColor: 'rgba(99,102,241,0.45)' }}>
              <Plus size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              New Plan
            </button>
          </div>
        </section>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', opacity: 0.6 }}>Loading…</div>
        ) : (
          <div style={{ overflowX: 'auto', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.04)', textAlign: 'left' }}>
                  <th style={th}>Plan Key</th>
                  <th style={th}>Display Name</th>
                  <th style={th}>Monthly</th>
                  <th style={th}>Yearly</th>
                  <th style={th}>Minutes</th>
                  <th style={th}>Active</th>
                  <th style={th}>Order</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans.length === 0 && (
                  <tr><td colSpan={8} style={{ ...td, textAlign: 'center', opacity: 0.6 }}>
                    No plans yet. Click "Seed Defaults" or "New Plan" to start.
                  </td></tr>
                )}
                {plans.map((p) => (
                  <tr key={p.id} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12, opacity: 0.8 }}>{p.planKey}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{p.displayName}</td>
                    <td style={td}>₹{p.priceMonthlyInr ?? Math.round((p.priceMonthlyPaise || 0) / 100)}</td>
                    <td style={td}>₹{p.priceYearlyInr ?? Math.round((p.priceYearlyPaise || 0) / 100)}</td>
                    <td style={td}>{p.minutesAllowed}</td>
                    <td style={td}>
                      <span style={{
                        padding: '3px 10px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 700,
                        background: p.isActive ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                        color: p.isActive ? '#4ade80' : '#f87171',
                      }}>
                        {p.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={td}>{p.sortOrder ?? 0}</td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={btnSm} onClick={() => openEdit(p)} title="Edit">
                          <Edit3 size={14} />
                        </button>
                        <button style={btnSm} onClick={() => handleToggle(p)} title={p.isActive ? 'Disable' : 'Enable'}>
                          {p.isActive ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {editing && (
        <div style={modalBackdrop} onClick={closeEdit}>
          <div style={modalShell} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ margin: 0 }}>{editing.id ? 'Edit Plan' : 'New Plan'}</h3>
              <button onClick={closeEdit} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <div style={field}>
              <label style={lbl}>Plan Key (e.g. Standard / Plus / Pro)</label>
              <input
                style={inp}
                value={editing.planKey}
                onChange={(e) => updateField('planKey', e.target.value)}
                disabled={!!editing.id}
              />
            </div>
            <div style={field}>
              <label style={lbl}>Display Name</label>
              <input style={inp} value={editing.displayName} onChange={(e) => updateField('displayName', e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={field}>
                <label style={lbl}>Monthly Price (paise)</label>
                <input
                  type="number"
                  style={inp}
                  value={editing.priceMonthlyPaise}
                  onChange={(e) => updateField('priceMonthlyPaise', e.target.value)}
                />
                <small style={small}>= ₹{Math.round((Number(editing.priceMonthlyPaise) || 0) / 100)}</small>
              </div>
              <div style={field}>
                <label style={lbl}>Yearly Price (paise)</label>
                <input
                  type="number"
                  style={inp}
                  value={editing.priceYearlyPaise}
                  onChange={(e) => updateField('priceYearlyPaise', e.target.value)}
                />
                <small style={small}>= ₹{Math.round((Number(editing.priceYearlyPaise) || 0) / 100)}</small>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={field}>
                <label style={lbl}>Minutes Allowed</label>
                <input
                  type="number"
                  style={inp}
                  value={editing.minutesAllowed}
                  onChange={(e) => updateField('minutesAllowed', e.target.value)}
                />
              </div>
              <div style={field}>
                <label style={lbl}>Sort Order</label>
                <input
                  type="number"
                  style={inp}
                  value={editing.sortOrder}
                  onChange={(e) => updateField('sortOrder', e.target.value)}
                />
              </div>
            </div>

            <div style={field}>
              <label style={lbl}>Features</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(editing.features || []).map((f, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6 }}>
                    <input
                      style={{ ...inp, flex: 1 }}
                      placeholder={`Feature ${i + 1}`}
                      value={f}
                      onChange={(e) => updateFeature(i, e.target.value)}
                    />
                    <button style={btnSm} onClick={() => removeFeature(i)} title="Remove">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <button style={{ ...btnSm, alignSelf: 'flex-start' }} onClick={addFeature}>
                  <Plus size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Add feature
                </button>
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!editing.isActive}
                onChange={(e) => updateField('isActive', e.target.checked)}
              />
              <span style={{ fontSize: 13 }}>Active (visible in pricing modal)</span>
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button style={btnSm} onClick={closeEdit}>Cancel</button>
              <button
                style={{ ...btnSm, background: 'rgba(99,102,241,0.25)', borderColor: 'rgba(99,102,241,0.5)' }}
                onClick={submit}
                disabled={saving}
              >
                <Save size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                {saving ? 'Saving…' : 'Save plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const th = { padding: '12px 14px', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', opacity: 0.75 };
const td = { padding: '14px', fontSize: 13 };
const field = { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 };
const lbl = { fontSize: 12, opacity: 0.75, fontWeight: 600 };
const inp = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.10)',
  color: '#fff',
  borderRadius: 8,
  padding: '9px 12px',
  fontSize: 13,
  outline: 'none',
  fontFamily: 'inherit',
};
const small = { fontSize: 11, opacity: 0.55 };
const btnSm = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.10)',
  color: '#fff',
  padding: '6px 10px',
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
};
const modalBackdrop = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  backdropFilter: 'blur(6px)',
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  overflowY: 'auto',
};
const modalShell = {
  background: '#0f0f1a',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 14,
  padding: 22,
  width: 'min(560px, 100%)',
  maxHeight: '90vh',
  overflowY: 'auto',
  color: '#fff',
};

export default AdminPlansPage;
